import { useState } from "react";
import { ClipboardCheck, Check, X, Loader2, Lightbulb, TrendingUp, Info, Undo2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const STATUS = {
  supported: { label: "Supported by your CV", cls: "text-kesh-text bg-kesh-soft border-kesh/25" },
  confirmed: { label: "Confirmed by you", cls: "text-brand-text bg-brand-soft border-brand/25" },
  needs_info: { label: "Additional information required", cls: "text-warning bg-warning-soft border-warning/30" },
  auto_added: { label: "Added automatically: please confirm", cls: "text-warning bg-transparent border-warning/50 border-dashed" },
  declined: { label: "Not added", cls: "text-body bg-transparent border-line-strong" },
};

// Mirrors backend _norm_gaps for applications created by the earlier auto-add version.
export function normGaps(g) {
  if (!g || g.version === 2) return g;
  const kept = new Set((g.kept || []).map((k) => k.toLowerCase()));
  return {
    version: 2, legacy: true, supported: [], ats_before: g.ats_before, ats_after: g.ats_after,
    items: (g.added || []).map((k) => ({ skill: k, status: kept.has(k.toLowerCase()) ? (g.acknowledged ? "confirmed" : "auto_added") : "declined" })),
  };
}

export const Badge = ({ status, testid }) => (
  <span data-testid={testid} className={`text-xs font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${STATUS[status].cls}`}>
    {STATUS[status].label}
  </span>
);

function GapRow({ item, i, busy, onAction }) {
  const [asking, setAsking] = useState(false);
  const [example, setExample] = useState("");
  const pending = item.status === "needs_info" || item.status === "auto_added";
  return (
    <div data-testid={`evidence-item-${i}`} data-status={item.status} className="rounded-lg border border-line bg-canvas p-3.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className={`font-medium ${item.status === "declined" ? "text-faint line-through" : "text-ink"}`}>{item.skill}</span>
        <Badge status={item.status} testid={`evidence-item-${i}-status`} />
        <div className="ml-auto flex items-center gap-2">
          {pending && !asking && (
            <>
              <button data-testid={`evidence-item-${i}-have`} disabled={busy} onClick={() => setAsking(true)}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border border-brand/40 bg-surface text-brand-text hover:bg-brand-soft transition-colors disabled:opacity-50">
                <Check className="w-3.5 h-3.5" /> I have this
              </button>
              <button data-testid={`evidence-item-${i}-dont`} disabled={busy} onClick={() => onAction(item.skill, "decline")}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border border-line text-body hover:bg-subtle transition-colors disabled:opacity-50">
                <X className="w-3.5 h-3.5" /> {item.status === "auto_added" ? "Remove" : "I don't"}
              </button>
            </>
          )}
          {!pending && (
            <button data-testid={`evidence-item-${i}-undo`} disabled={busy} onClick={() => onAction(item.skill, "reset")}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs text-body hover:text-ink transition-colors disabled:opacity-50">
              <Undo2 className="w-3.5 h-3.5" /> Undo
            </button>
          )}
          {busy && <Loader2 className="w-4 h-4 animate-spin text-body" />}
        </div>
      </div>
      {item.status === "confirmed" && item.example && (
        <p className="text-xs text-body mt-2" data-testid={`evidence-item-${i}-example`}>Your example: {item.example}</p>
      )}
      {asking && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <label htmlFor={`ev-ex-${i}`} className="sr-only">Where have you used {item.skill}?</label>
          <input id={`ev-ex-${i}`} data-testid={`evidence-item-${i}-example-input`} value={example} maxLength={400} autoFocus
            onChange={(e) => setExample(e.target.value)}
            placeholder={`Optional: where have you used ${item.skill}? (job, project, course…)`}
            className="flex-1 bg-surface border border-line-strong rounded-lg px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand" />
          <div className="flex gap-2">
            <button data-testid={`evidence-item-${i}-confirm`} disabled={busy} onClick={async () => { await onAction(item.skill, "confirm", example); setAsking(false); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-brand text-onbrand hover:bg-brand-hover transition-colors disabled:opacity-50">
              Add to my CV
            </button>
            <button onClick={() => setAsking(false)} className="px-3 py-2 rounded-lg text-sm text-body hover:text-ink">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SkillGapsBox({ app, gaps, supported, onApplied, onCoach }) {
  const [busy, setBusy] = useState(null);
  const pending = gaps.items.filter((i) => i.status === "needs_info" || i.status === "auto_added").length;
  const [open, setOpen] = useState(pending > 0);

  const act = async (skill, action, example = "") => {
    setBusy(skill);
    try {
      const { data } = await api.patch(`/applications/${app.id}/skill-gaps`, { skill, action, example });
      onApplied(data);
      const msg = { confirm: `Added "${skill}" to your CV`, decline: `"${skill}" won't be added`, reset: `"${skill}" needs your input again` };
      toast.success(msg[action]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update your CV");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="evidence-checker" id="evidence-checker" className={`rounded-xl border p-5 sm:p-6 scroll-mt-24 ${pending ? "border-info/40 bg-info-soft" : "border-line bg-surface"}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid="evidence-checker-toggle" className="w-full flex flex-wrap items-center gap-3 text-left" aria-expanded={open}>
        <div className="w-10 h-10 rounded-lg bg-info-soft border border-info/30 flex items-center justify-center shrink-0">
          <ClipboardCheck className="w-5 h-5 text-info" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-display text-lg font-bold text-ink" data-testid="evidence-checker-title">
            {pending ? `Evidence Checker: ${pending} job requirement${pending === 1 ? "" : "s"} need${pending === 1 ? "s" : ""} your input` : "Evidence Checker: all requirements reviewed"}
          </h3>
          <p className="text-sm text-body mt-0.5">
            {pending ? "The job asks for these, but your CV doesn't show them. We only add what you confirm." : `${gaps.items.filter((i) => i.status === "confirmed").length} confirmed by you and added to your CV.`}
          </p>
        </div>
        <span data-testid="evidence-checker-score" className="flex items-center gap-2 px-3 py-2 rounded-lg border border-line bg-canvas font-mono2 text-sm">
          <TrendingUp className="w-4 h-4 text-brand" />
          <span className="text-body">Match</span> <span className="text-body">{gaps.ats_before}%</span>
          <span className="text-faint">→</span> <span className="text-brand font-semibold" data-testid="evidence-checker-score-after">{gaps.ats_after}%</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-faint transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-5 space-y-4 af-fade-up">
          <div className="space-y-2" data-testid="evidence-checker-list">
            {gaps.items.map((it, i) => <GapRow key={it.skill} item={it} i={i} busy={busy === it.skill} onAction={act} />)}
          </div>

          {supported?.length > 0 && (
            <div data-testid="evidence-supported">
              <div className="text-xs font-mono2 uppercase tracking-widest text-faint mb-2">Already supported by your CV</div>
              <div className="flex flex-wrap gap-1.5">
                {supported.map((k) => <span key={k} className="text-xs px-2.5 py-1 rounded-full border border-brand/30 bg-brand-soft text-brand">{k}</span>)}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-line bg-canvas p-4 text-sm text-body leading-relaxed flex gap-3" data-testid="evidence-checker-warning">
            <Info className="w-4 h-4 text-info mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold text-ink">Only confirm what you can back up. </span>
              Interviewers often ask about every skill on your CV. Confirm a skill only if you can give a real example from work, study, projects or volunteering. If you don't have it yet, leave it out: honest gaps are better than claims that fall apart at interview.
            </div>
          </div>

          <button data-testid="evidence-checker-coach-button" onClick={onCoach}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-line text-ink hover:bg-subtle transition-colors">
            <Lightbulb className="w-4 h-4 text-warning" /> How to close these gaps
          </button>
        </div>
      )}
    </div>
  );
}
